import {useParams} from 'react-router-dom';
import clsx from 'clsx';
import {Trans} from '@ui/i18n/trans';
import {StaticPageTitle} from '@common/seo/static-page-title';
import React, {Fragment, ReactNode, useRef} from 'react';
import {useStickySentinel} from '@ui/utils/hooks/sticky-sentinel';
import {useScrollToTop} from '@common/ui/navigation/use-scroll-to-top';
import {useTitle} from '@app/titles/requests/use-title';
import {FullPageLoader} from '@ui/progress/full-page-loader';

interface Props {
  children: ReactNode;
  actions?: ReactNode;
}

export function LienEditorLayout({children, actions}: Props) {
  const {isSticky, sentinelRef} = useStickySentinel();
  const {lienId, nzbId, torrentId} = useParams();
  const ref = useRef<HTMLDivElement>(null);
  const title = useTitle('title');
  useScrollToTop(ref);
  if (!title.data) {
    return <FullPageLoader />;
  }
  const model = lienId?.length ? 'lien' : nzbId?.length ? 'nzb' : 'torrent';
  const heading =
    lienId || nzbId || torrentId ? (
      <Trans
        values={{
          id: lienId || nzbId || torrentId,
          title: title.data.title.name,
        }}
        message={`Edit ${model} Id “:id“ on :title`}
      />
    ) : (
      <Trans
        message="New lien on :title"
        values={{title: title.data.title.name}}
      />
    );

  return (
    <Fragment>
      <StaticPageTitle>{heading}</StaticPageTitle>
      <div ref={sentinelRef} />
      <div
        ref={ref}
        className={clsx(
          'sticky top-0 z-10 my-12 transition-shadow md:my-24',
          isSticky && 'bg-paper shadow',
        )}
      >
        <div
          className={clsx(
            'container mx-auto flex items-center gap-24 px-24 py-14 md:items-start',
          )}
        >
          <h1 className="overflow-hidden overflow-ellipsis whitespace-nowrap text-xl md:mr-64 md:text-3xl">
            {heading}
          </h1>
          <div className="mr-auto"></div>
          {actions}
        </div>
      </div>
      <div className="container mx-auto items-stretch gap-30 px-24 pb-24 md:flex">
        <div className="relative flex-auto md:pl-30">{children}</div>
      </div>
    </Fragment>
  );
}
